import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Save, AlertTriangle, CheckCircle, Package, MessageSquare } from 'lucide-react';
import Layout from './Layout';
import { useStore } from '../context/StoreContext';
import { useAlert } from '../context/AlertContext';
import './Management.css';

const Inventory = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const queryDate = queryParams.get('date');

    const { getStore, updateStore, user, stores, currentDate } = useStore();
    const { showAlert } = useAlert();
    const store = getStore(id);
    const today = currentDate;
    const targetDate = queryDate || today;

    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    const isPastCutoff = currentTime.getHours() === 23 ? currentTime.getMinutes() >= 50 : currentTime.getHours() > 23;
    const isMyStore = user?.role === 'admin' || String(user?.storeId) === String(id);

    // 월간 마감 잠금 체크
    const todayObj = new Date(today);
    const targetObj = new Date(targetDate);
    const isMonthLocked = (targetObj.getFullYear() < todayObj.getFullYear()) ||
        (targetObj.getFullYear() === todayObj.getFullYear() && targetObj.getMonth() < todayObj.getMonth());

    const canEdit = !isMonthLocked && ((user?.role === 'admin') || (targetDate === today && isMyStore && !isPastCutoff));
    const isReadOnlyWarning = !isMonthLocked && targetDate === today && isMyStore && isPastCutoff && user?.role !== 'admin';

    const [items, setItems] = useState([]);
    const [notes, setNotes] = useState('');
    const [saveMessage, setSaveMessage] = useState(false);
    const [currentStoreName, setCurrentStoreName] = useState('');

    // 보안 권한 체크: 로그인 필수 및 본인 매장 확인
    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        // 관리자가 아니고 본인 매장 ID와 다르면 접근 제한 (보안 강화)
        if (user.role !== 'admin' && String(user.storeId) !== String(id)) {
            showAlert('자신이 담당하는 매장의 재고 현황만 관리할 수 있습니다.');
            navigate('/dashboard');
        }
    }, [user, navigate, id]);

    useEffect(() => {
        const storeData = getStore(id);
        if (!storeData) return;

        if (currentStoreName !== storeData.name) {
            setCurrentStoreName(storeData.name);
        }
        const defaultNames = ['핫도그 번', '소시지 (10개입)', '생수 500ml', '펩시 콜라 355ml', '종이컵 (L)', '케첩/머스타드 (팩)', '종이 트레이'];
        const itemNames = (storeData.items && storeData.items.length > 0) ? storeData.items : defaultNames;

        // 타겟 날짜의 기록이 있으면 가져오고, 없으면 입력을 위해 빈 배열 상태로 설정 (초기값 0)
        const history = storeData.inventoryHistory?.[targetDate];
        const stocks = history?.stocks || [];
        const savedNotes = history?.notes || '';

        // 이전 날짜 중 가장 최근 기록 찾기 (증감 표시용)
        const historyDates = Object.keys(storeData.inventoryHistory || {})
            .filter(d => d < targetDate)
            .sort((a, b) => b.localeCompare(a));
        const prevDate = historyDates[0];
        const prevHistory = prevDate ? storeData.inventoryHistory[prevDate] : null;
        // 이전 기록이 없을 경우 가장 최근 재고 데이터(inventoryData.stocks)를 전일 재고로 사용
        const prevStocks = prevHistory?.stocks || storeData.inventoryData?.stocks || [];

        // ⚠️ 과거 데이터 보호: 히스토리에 저장된 itemNames가 있으면 우선 사용
        // 단, 조회하려는 날짜가 오늘(또는 미래)인 경우에는 현재 매장 설정(storeData.items)을 우선 사용
        const isTodayOrFuture = targetDate >= today;
        const displayNames = (history?.itemNames && history.itemNames.length > 0 && !isTodayOrFuture)
            ? history.itemNames
            : itemNames;

        const initialData = displayNames.map((name, index) => {
            const previousStock = (prevStocks && prevStocks[index] !== undefined) ? Number(prevStocks[index]) : 0;
            const currentStock = history ? (Number(stocks[index]) || 0) : previousStock;
            const Diff = currentStock - previousStock;

            return {
                id: index + 1,
                name: name || `품목 ${index + 1}`,
                stock: (currentStock === 0 && !history) ? previousStock : currentStock,
                prevStock: previousStock,
                diff: Diff,
                unit: '박스',
                threshold: 0
            };
        });

        setItems(prev => {
            if (JSON.stringify(prev) !== JSON.stringify(initialData)) return initialData;
            return prev;
        });
        
        if (notes !== savedNotes) {
            setNotes(savedNotes);
        }
        // id나 targetDate가 바뀔 때만 초기화하여 저장 시의 store 객체 변화로 인한 리셋을 방지
    }, [id, targetDate, getStore, currentStoreName]);

    const handleUpdate = (itemId, newStock) => {
        // 비어있는 입력값 허용 (사용자가 다 지웠을 때 0으로 리셋 방지)
        const val = newStock === '' ? '' : Number(newStock);

        setItems(prevItems => {
            if (!Array.isArray(prevItems)) return [];
            return prevItems.map(item => {
                if (item.id === itemId) {
                    // 계산용 숫자값 (비어있으면 0)
                    const numericVal = val === '' ? 0 : Number(val);
                    const prevVal = Number(item.prevStock) || 0;
                    return {
                        ...item,
                        stock: val,
                        hasChanged: numericVal !== prevVal,
                        diff: numericVal - prevVal
                    };
                }
                return item;
            });
        });
    };

    const isInventoryUnchanged = items.every(item => (Number(item.stock) || 0) === (Number(item.prevStock) || 0));

    // 당일 매출 총액 확인
    const todaySalesRecord = store?.salesHistory?.[targetDate] ||
        store?.salesData?.find(s => (s.localDate || s.date.split('T')[0]) === targetDate);
    const todayTotalSales = todaySalesRecord?.total || 0;

    // 검증 실패 조건: 매출은 있는데 재고 변동이 전혀 없는 경우
    const isValidationError = todayTotalSales > 0 && isInventoryUnchanged;

    const handleSave = () => {
        const inventoryHistory = store?.inventoryHistory || {};
        const isEditing = !!inventoryHistory[targetDate];

        if (isValidationError) {
            showAlert('⚠️ 매출이 발생했으나 재고 변동 내역이 없습니다.\n이전 기록과 동일하게 제출할 수 없습니다. 정확한 실재고를 파악하여 입력해 주세요.');
            return;
        }

        // 검증 로직: 수정 시 또는 관리자(Master 포함) 최초 입력 시 비고 필수
        const currentUserId = (user?.id || '').toLowerCase();
        const currentUserRole = (user?.role || '').toLowerCase();
        const isAdminAccount = currentUserRole === 'admin' || currentUserId === 'master' || currentUserId.startsWith('admin');
        
        if (isEditing || isAdminAccount) {
            if (!notes || notes.trim() === '') {
                const alertMsg = isEditing 
                    ? '💡 보다 정확한 데이터 관리를 위해 하단 비고란에 구체적인 수정 사유를 입력해 주세요.\n(예: 오타 수정, 재고 실사 후 수량 변경 등)'
                    : '📢 관리자 대면/대리 입력 시에는 반드시 하단 비고란에 해당 사유를 입력해 주시기 바랍니다.\n(예: 매니저 요청 대리 입력, 현장 점검 후 입력 등)';
                showAlert(alertMsg);
                return;
            }
            if (isEditing && notes.trim() === (inventoryHistory[targetDate].notes || '').trim()) {
                showAlert('✍️ 이전 기록과 사유가 동일한 내용입니다. 변경된 부분에 대한 구체적인 사유를 추가로 작성해 주세요.\n(예: 사유 보충, 변경 사항 상세 기술 등)');
                return;
            }
        }

        const stocksToSave = items.map(item => item.stock);
        const dailyStatus = store?.dailyStatus || {};
        const currentDayStatus = dailyStatus[targetDate] || {};
        const todayStr = targetDate;

        updateStore(id, {
            inventoryData: { stocks: stocksToSave }, // 최신 데이터 유지
            inventoryHistory: {
                ...inventoryHistory,
                [targetDate]: {
                    stocks: stocksToSave,
                    // ⚠️ 과거 데이터 보호: 저장 당시의 품목 이름도 함께 저장
                    // 나중에 store.items를 변경해도 이 날짜의 기록은 저장당시 이름으로 표시됨
                    itemNames: store?.items || [],
                    notes: notes,
                    isEdited: !!inventoryHistory[targetDate] || inventoryHistory[targetDate]?.isEdited,
                    lastModified: !!inventoryHistory[targetDate] ? new Date().toISOString() : null
                } // 날짜별 히스토리 저장
            },
            dailyStatus: {
                ...dailyStatus,
                [targetDate]: {
                    ...currentDayStatus,
                    inventory: true
                }
            }
        });

        setSaveMessage(true);
        setTimeout(() => {
            setSaveMessage(false);
            // 시설 현황으로 이동
            navigate(`/store/${id}/facility`);
        }, 2000);
    };

    const formattedDate = targetDate.replace(/-/g, '. ');

    return (
        <Layout>
            <div className="management-content">
                <div className="management-header-flex">
                    <h1>재고 현황 <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>v2.5.1</span> <span>- {store?.name || ''}</span></h1>
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div className="mobile-store-name">{currentStoreName}</div>
                        <div className="date-display-badge">
                            <Package size={18} />
                            <span>{formattedDate}</span>
                            {store?.inventoryHistory?.[targetDate]?.isEdited && (
                                <span style={{
                                    fontSize: '0.7rem',
                                    padding: '2px 6px',
                                    background: 'rgba(234, 179, 8, 0.2)',
                                    color: '#facc15',
                                    borderRadius: '4px',
                                    fontWeight: '800',
                                    marginLeft: '8px'
                                }}>
                                    수정됨
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {saveMessage && (
                    <div className="success-overlay">
                        <CheckCircle size={24} />
                        <span>데이터가 성공적으로 전송되었습니다!</span>
                    </div>
                )}

                {isMonthLocked && (
                    <div className="admin-only-alert" style={{ marginBottom: '20px', backgroundColor: 'rgba(148, 163, 184, 0.15)', borderColor: '#94a3b8', color: '#94a3b8' }}>
                        <Save size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                        해당 월의 데이터는 월간 마감이 완료되어 수정할 수 없는 보관 상태입니다.
                    </div>
                )}

                {/* 최근 3일 추이 요약 (모바일/PC 공통 3칸 레이아웃 강제) */}
                <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '20px',
                    width: '100%'
                }}>
                    {[1, 2, 3].map(daysAgo => {
                        const d = new Date(targetDate);
                        d.setDate(d.getDate() - daysAgo);
                        const dateStr = d.getFullYear() + '-' +
                            String(d.getMonth() + 1).padStart(2, '0') + '-' +
                            String(d.getDate()).padStart(2, '0');

                        const history = store?.inventoryHistory?.[dateStr];
                        const stocks = history?.stocks || [];
                        const shortages = stocks.filter(s => (s !== undefined ? s : 0) < 1).length || 0;

                        return (
                            <div key={dateStr} className="glass-card" style={{
                                flex: 1,
                                padding: '10px 4px',
                                textAlign: 'center',
                                opacity: 0.8,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}>
                                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '2px' }}>
                                    {dateStr.slice(5).replace('-', '/')}
                                </div>
                                <div style={{
                                    fontSize: '0.8rem',
                                    fontWeight: '700',
                                    color: shortages > 0 ? '#ef4444' : (history ? '#10b981' : '#64748b'),
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {history ? (shortages > 0 ? `부족 ${shortages}` : '정상') : '기록없음'}
                                </div>
                            </div>
                        );
                    })}
                </div>


                {isReadOnlyWarning && (
                    <div className="admin-only-alert" style={{ marginBottom: '20px', backgroundColor: 'rgba(249, 115, 22, 0.15)', borderColor: '#f97316', color: '#f97316' }}>
                        <AlertCircle size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                        금일 보고는 23:50에 마감되었습니다. 이후 수정은 관리자에게 문의하세요.
                    </div>
                )}

                {isValidationError && (
                    <div className="admin-only-alert" style={{
                        marginBottom: '20px',
                        backgroundColor: 'rgba(239, 68, 68, 0.15)',
                        borderColor: '#ef4444',
                        color: '#ef4444',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px'
                    }}>
                        <AlertTriangle size={24} />
                        <div>
                            <strong style={{ display: 'block', marginBottom: '4px' }}>재고 파악 오류 주의</strong>
                            <span>금일 매출이 발생했으나 입력된 재고가 이전 기록과 100% 일치합니다. 정확한 재고 수량을 다시 확인해 주세요.</span>
                        </div>
                    </div>
                )}

                <div className="card-header" style={{ marginBottom: '10px' }}>
                    <div className="status-indicator-group">
                        {store?.dailyStatus?.[targetDate]?.sales ? (
                            <div className="submit-badge sales-done">
                                <CheckCircle size={16} /> 매출 완료
                            </div>
                        ) : (
                            <div className="submit-badge pending">
                                매출 대기
                            </div>
                        )}
                        {store?.dailyStatus?.[targetDate]?.inventory ? (
                            <div className="submit-badge inventory-done">
                                <CheckCircle size={16} /> 재고 완료
                            </div>
                        ) : (
                            <div className="submit-badge pending">
                                재고 대기
                            </div>
                        )}
                    </div>


                </div>

                <div className="glass-card management-card">
                    <div className="inventory-info-header" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '24px' }}>
                        <Package size={20} color="#38bdf8" style={{ marginTop: '2px' }} />
                        <div>
                            <p style={{ margin: 0, color: '#cbd5e1' }}>{id}번 매장에 설정된 {items.length}개 품목의 실시간 재고를 관리합니다.</p>
                            <p style={{ margin: '4px 0 0 0', color: '#94a3b8', fontSize: '0.85rem' }}>* 각 품목의 현재 재고가 1박스 미만일 경우 부족으로 표시됩니다.</p>
                        </div>
                    </div>

                    {/* Mobile Card View (shown only on mobile) */}
                    <div className="inventory-mobile-list">
                        {items.map(item => (
                            <div key={item.id} className="inventory-mobile-card">
                                <div className="inventory-card-flex">
                                    <div className="inventory-card-info">
                                        <h4>{item.name}</h4>
                                        <p style={{ color: item.stock <= item.threshold ? '#f87171' : '#94a3b8' }}>
                                            전일: {item.prevStock} {item.unit} |
                                            {item.diff !== 0 && (
                                                <span style={{ marginLeft: '6px', color: item.diff > 0 ? '#4ade80' : '#f87171' }}>
                                                    ({item.diff > 0 ? '+' : ''}{item.diff})
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <div className="quantity-control">
                                        <button
                                            type="button"
                                            className="qty-btn"
                                            onClick={() => canEdit && handleUpdate(item.id, Math.max(0, item.stock - 1))}
                                            disabled={!canEdit}
                                        >
                                            -
                                        </button>
                                        <input
                                            type="number"
                                            className="qty-input"
                                            value={item.stock}
                                            onChange={(e) => handleUpdate(item.id, e.target.value)}
                                            readOnly={!canEdit}
                                        />
                                        <button
                                            type="button"
                                            className="qty-btn"
                                            onClick={() => canEdit && handleUpdate(item.id, item.stock + 1)}
                                            disabled={!canEdit}
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                                {item.stock <= item.threshold && (
                                    <div style={{ marginTop: '10px', fontSize: '0.75rem', color: '#f87171', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <AlertTriangle size={12} /> 재고 부족 (기준: {item.threshold}{item.unit} 이하)
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Desktop Table View (shown only on desktop) */}
                    <div className="inventory-desktop-table recent-activities" style={{ background: 'transparent', padding: '0', border: 'none' }}>
                        <div className="activities-list is-admin">
                            <div className="activity-item header-item" style={{
                                gridTemplateColumns: '1.5fr 1fr 1.5fr 1fr 1fr 120px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                padding: '12px 20px',
                                borderRadius: '12px 12px 0 0'
                            }}>
                                <div>품목명</div>
                                <div style={{ textAlign: 'center' }}>전일 재고</div>
                                <div style={{ textAlign: 'center' }}>현재 재고</div>
                                <div style={{ textAlign: 'center' }}>증감</div>
                                <div style={{ textAlign: 'center' }}>단위</div>
                                <div style={{ textAlign: 'center' }}>상태</div>
                            </div>
                            {items.map(item => (
                                <div key={item.id} className="activity-item row-item" style={{
                                    gridTemplateColumns: '1.5fr 1fr 1.5fr 1fr 1fr 120px',
                                    padding: '16px 20px',
                                    alignItems: 'center',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                                }}>
                                    <div style={{ fontWeight: '600', color: '#f8fafc' }}>{item.name}</div>
                                    <div style={{ textAlign: 'center', color: '#94a3b8' }}>{item.prevStock} {item.unit}</div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div className="quantity-control" style={{ margin: '0 auto', width: 'fit-content' }}>
                                            <button type="button" className="qty-btn" onClick={() => canEdit && handleUpdate(item.id, Math.max(0, item.stock - 1))} disabled={!canEdit}>-</button>
                                            <input type="number" className="qty-input" value={item.stock} onChange={(e) => handleUpdate(item.id, e.target.value)} readOnly={!canEdit} />
                                            <button type="button" className="qty-btn" onClick={() => canEdit && handleUpdate(item.id, item.stock + 1)} disabled={!canEdit}>+</button>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'center', color: item.diff > 0 ? '#4ade80' : (item.diff < 0 ? '#f87171' : '#94a3b8'), fontWeight: '600' }}>
                                        {item.diff > 0 ? '+' : ''}{item.diff}
                                    </div>
                                    <div style={{ textAlign: 'center', color: '#64748b' }}>{item.unit}</div>
                                    <div style={{ textAlign: 'center' }}>
                                        {item.stock <= item.threshold ? (
                                            <span style={{ fontSize: '0.75rem', color: '#f87171', background: 'rgba(239, 68, 68, 0.1)', padding: '4px 8px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <AlertTriangle size={12} /> 부족
                                            </span>
                                        ) : (
                                            <span style={{ fontSize: '0.75rem', color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '4px 8px', borderRadius: '4px' }}>
                                                정상
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="notes-section" style={{ marginTop: '24px' }}>
                        <label>
                            <MessageSquare size={18} /> 
                            재고 특이사항 {store?.inventoryHistory?.[targetDate] ? <span style={{ color: '#ef4444', marginLeft: '4px' }}>(수정 시 필수입력)</span> : <span style={{ color: '#94a3b8', marginLeft: '4px' }}>(선택사항)</span>}
                        </label>
                        <textarea
                            className="notes-textarea"
                            placeholder="재고 변동 원인이나 품질 이슈가 있다면 입력해 주세요."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            disabled={!canEdit}
                        />
                    </div>

                    <button
                        className={`full-width-button ${isValidationError ? 'danger' : ''}`}
                        onClick={handleSave}
                        style={{ marginTop: '24px' }}
                    >
                        <Save size={18} style={{ marginRight: '8px' }} />
                        {isValidationError ? '재고 정확히 파악 후 다시 저장' : `${currentStoreName || '매장'} 재고 수량 저장`}
                    </button>
                </div>
            </div>
        </Layout>
    );
};

export default Inventory;

